import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CashierPaymentsWidgetLazy } from '@wix/cashier-payments-widget/lazy';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import {
  getDealInfoByType,
  delayedRetryable,
} from '@wix/wix-vod-shared/common';
import { ClampText } from '@wix/wix-vod-shared/components';
import { withTranslation, withFedopsLogger } from '@wix/yoshi-flow-editor';
import Spinner from '@wix/wix-vod-shared/dist/esm/widget/ui-components/loading-spinner/loading-spinner';

import { logBi } from '../../../../worker/actions/bi';
import ResizedImage from '../../../resized-image/resized-image';
import { getVisitorId } from '../../../../bi/bi-vod';
import IconButton from '../../../buttons/icon/icon';
import EVENTS from '../../../../constants/events';
import {
  getInstanceId,
  getInstance,
  getCompId,
} from '../../../../redux/hydrated-data/hydrated-data';
import { PaymentType } from '@wix/wix-vod-constants/api-types';
import {
  isStatusSuccess,
  isStatusPending,
  isStatusCanceled,
} from '../../../../constants/payment-statuses';
import PAYMENT_MESSAGE_PROXY from '../../../../constants/payment-message-proxy';
import {
  getCurrentChannelId,
  getChannelId,
} from '../../../../selectors/channels';
import { getMemberInfo } from '../../../../api/public/channel/channel';
import {
  initPayment,
  selectPaymentMethod,
  setError,
  statusChange,
} from '../../../../redux/actions/payment';
import DealInfo from '../../../payments/deal-info';
import CurrentSiteUser from '../../../account/current-site-user';
import LogOutLink from '../../../account/logout-link';
import { getCurrentUserEmail } from '../../../../selectors/current-site-user';
import { getOrderId } from '../../../../selectors/payment';
import styles from './payments-page.scss';
import { pubSubPublish } from '../../../../worker/actions/pub-sub/publish';
import { getCurrentLocale } from '../../../../selectors/locale';
import { getPublicApi } from '../../../../api/public';
import { getBaseUrl } from '../../../../selectors/api';
import { VIDEO_APP_DEF_ID } from '@wix/wix-vod-constants/common';
import { FedopsInteractionsNames } from '../../../../constants/fedops-interaction-names';

const isSubscription = (paymentType) =>
  paymentType === PaymentType.SUBSCRIPTION;

const COVER_WIDTH = 72;
const COVER_HEIGHT = 68;

const broadcastPaymentEvent = (itemId, paymentType) => (dispatch, getState) => {
  const compId = getCompId(getState());

  const data = {
    id: PAYMENT_MESSAGE_PROXY.ID,
    compId,
    message: {
      completed: true,
      itemId: paymentType === PaymentType.SUBSCRIPTION ? null : itemId,
      type: paymentType,
    },
  };

  dispatch(pubSubPublish(EVENTS.PAYMENT.PAYMENT_EVENT, data));
};

const verifyPaymentWithWixVod = () => async (dispatch, getState) => {
  const state = getState();
  const channelId = getChannelId(state) || getCurrentChannelId(state);
  const instance = getInstance(state);
  const baseUrl = getBaseUrl(state);

  const { data } = await getMemberInfo(baseUrl, channelId, instance);

  return data.items.find(
    ({ dgsInfo, listId, itemId }) =>
      listId === channelId && !itemId && dgsInfo[PaymentType.SUBSCRIPTION],
  );
};

const mapDispatchToProps = {
  initPayment,
  selectPaymentMethod,
  setPaymentError: setError,
  changePaymentStatus: statusChange,
  logBi,
  broadcastPaymentEvent,
  verifyPaymentWithWixVod,
};

const mapStateToProps = (state, { itemData, paymentType }) => ({
  instance: getInstance(state),
  instanceId: getInstanceId(state),
  dealInfo: getDealInfoByType(itemData.dealInfo, paymentType),
  channelId: getChannelId(state) || getCurrentChannelId(state),
  orderId: getOrderId(state),
  userEmail: getCurrentUserEmail(state),
  locale: getCurrentLocale(state),
});

class PaymentsPage extends React.Component {
  static propTypes = {
    paymentType: PropTypes.string.isRequired,
    itemData: PropTypes.object.isRequired,
    instanceId: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    handleReturnClick: PropTypes.func.isRequired,
    onLogOut: PropTypes.func.isRequired,
    onPaymentStart: PropTypes.func.isRequired,
    onPaymentComplete: PropTypes.func.isRequired,
    onPaymentMethodChange: PropTypes.func,
    orderId: PropTypes.string,
    instance: PropTypes.string,
    userEmail: PropTypes.string,
  };

  static defaultProps = {
    onPaymentMethodChange: _.noop,
    onPaymentComplete: _.noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      showLoader: !props.orderId,
    };
    this.visitorId = getVisitorId();
  }

  componentDidMount() {
    const { channelId, itemData, paymentType, initPayment } = this.props;

    this.props.fedops.interactionEnded(
      FedopsInteractionsNames.CASHIER_PAYMENT_PAGE_LOAD,
    );

    initPayment({
      ...(!isSubscription(paymentType) && {
        videoId: itemData.id,
      }),
      paymentType,
      channelId,
    });
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.orderId && this.props.orderId) {
      this.setState({ showLoader: false });
    }
  }

  handlePaymentMethodChange = (method) => {
    const {
      selectPaymentMethod,
      onPaymentMethodChange,
      channelId,
      dealInfo,
      paymentType,
    } = this.props;

    const { currency, price } = dealInfo;

    this.props.logBi('widget.paymentProvider.clicked', {
      channelID: channelId,
      paymentProvider: _.snakeCase(method),
      buyType: paymentType,
      currency,
      price: price * 1e5,
    });

    selectPaymentMethod(method);
    onPaymentMethodChange(method);
  };

  verifyPaymentWithDgs = async (transactionId) => {
    const { orderId, instance, paymentType, channelId, itemData } = this.props;
    const publicApi = getPublicApi();

    for (let i = 0; i < 10; i++) {
      const status = await publicApi.purchase.getPaymentStatus(
        itemData.id,
        channelId,
        orderId,
        transactionId,
        instance,
        paymentType,
      );

      if (isStatusSuccess(status)) {
        return status;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
  };

  onPaymentComplete = (method, cashierPaymentResult) => {
    this.props.fedops.interactionStarted(
      FedopsInteractionsNames.CASHIER_PAYMENT_COMPLETE_PAGE_LOAD,
    );
    this.props.onPaymentComplete(method, cashierPaymentResult);
  };

  handlePaymentComplete = async (method, cashierPaymentResult) => {
    const {
      itemData,
      paymentType,
      channelId,
      changePaymentStatus,
      setPaymentError,
    } = this.props;

    const { chargeId, status, errorCode } = cashierPaymentResult;

    if (errorCode) {
      setPaymentError(cashierPaymentResult);
      return;
    }

    changePaymentStatus(status);

    if (
      isStatusPending(status) ||
      (method === PaymentMethod.ConvenienceStore && isStatusSuccess(status))
    ) {
      this.onPaymentComplete(method, cashierPaymentResult);
      return;
    }

    if (isStatusCanceled(status)) {
      return;
    }

    // To update video status in video item we need to recheck payment status with DGS

    this.setState({ showLoader: true });

    const dgsStatus = await this.verifyPaymentWithDgs(chargeId);

    if (isStatusSuccess(dgsStatus)) {
      if (isSubscription(paymentType)) {
        /**
         * Because of race condition between cashier, DGS and wix-vod backends, last one
         * sometimes don't have much time to update channel with information about
         * purchase received from DGS.
         * So we send requests again until wix-vod backend will update item
         */
        await delayedRetryable(this.props.verifyPaymentWithWixVod, 20, 2000);
      }

      this.props.broadcastPaymentEvent(itemData.id || channelId, paymentType);
      this.onPaymentComplete(method, cashierPaymentResult);
    }
  };

  renderMainContent = () => {
    const {
      itemData: { customCoverUrl, coverUrl, title },
      instanceId,
      user,
      onLogOut,
      handleReturnClick,
      onPaymentStart,
      orderId,
      isMobile,
      dealInfo,
      userEmail,
      locale,
    } = this.props;

    return (
      <React.Fragment>
        <IconButton
          name="arrow-strip"
          size={16}
          roundedRect={false}
          className={styles['back-button']}
          iconClassName={styles['back-button-icon']}
          ariaLabel={this.props.t('widget.payments.back-button')}
          dataHook="back-button"
          onClick={handleReturnClick}
        >
          {isMobile ? null : (
            <span className={styles.backButtonText}>
              {this.props.t('widget.payments.back-button')}
            </span>
          )}
        </IconButton>
        <section
          className={classnames(styles['content-wrapper'], styles['item-info'])}
        >
          <ResizedImage
            className={styles.image}
            src={customCoverUrl || coverUrl}
            width={COVER_WIDTH}
            height={COVER_HEIGHT}
          />

          <div className={styles['item-description']}>
            <h2 className={styles['video-title-wrapper']}>
              <ClampText text={title} className={styles['video-title']} />
            </h2>

            <span className={styles['video-price']}>
              <DealInfo {...dealInfo} />
            </span>
          </div>
        </section>
        <div className={styles['content-wrapper']}>
          <div className={styles.delimiter} />
        </div>
        <section
          className={classnames(
            styles['content-wrapper'],
            styles['logout-wrapper'],
          )}
        >
          <CurrentSiteUser user={user} className={styles['current-user']} />
          <LogOutLink onLogout={onLogOut} className={styles.logout} />
        </section>

        <section className={styles['content-wrapper']}>
          <CashierPaymentsWidgetLazy
            creditCardPaymentInNewTab
            dataHook="cashier-payments-widget"
            onPaymentStart={onPaymentStart}
            onPaymentComplete={this.handlePaymentComplete}
            paymentMethodChanged={this.handlePaymentMethodChange}
            mandatoryFields={{ email: userEmail }}
            configuration={{
              appId: VIDEO_APP_DEF_ID,
              appInstanceId: instanceId,
              locale,
              visitorId: this.visitorId,
              snapshotId: orderId,
              currency: dealInfo.currency,
              amount: String(dealInfo.price),
            }}
          />
        </section>
      </React.Fragment>
    );
  };

  render() {
    const { isMobile } = this.props;
    const { showLoader } = this.state;

    const showScrollbar = !showLoader && !isMobile;

    return (
      <section
        className={classnames(
          styles['payments-page-wrapper'],
          styles.responsive,
          {
            [styles['with-scrollbar']]: showScrollbar,
          },
        )}
      >
        <div
          data-hook="payments-page"
          className={classnames(
            styles['payments-page-content'],
            styles.responsive,
            {
              [styles['is-mobile']]: isMobile,
            },
          )}
        >
          {showLoader ? (
            <Spinner dataHook="loader" className={styles.spinner} />
          ) : (
            this.renderMainContent()
          )}
        </div>
      </section>
    );
  }
}

export default withFedopsLogger(
  withTranslation()(connect(mapStateToProps, mapDispatchToProps)(PaymentsPage)),
);
